import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Container, Dialog, Grid, Popover, Slide } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

//import general components
import { BaseButton } from "../../../common/base-button/base-button";
import AutoCompleteComponent from "../../../common/base-select/autocomplete-component.js";
import BasePopover from "../../../common/base-popover/base-popover";

//import images and icons
import { ArrowDown2, ArrowUp2, Clock, Edit2 } from "iconsax-react";

//import styles
import "./itinerary-time.scss";

function ItineraryTime({ open, setOpen, handleTimeChange }) {
    //general
    const { control } = useForm();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();

    //sessionStorage variables
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));

    //time list
    const timeList = [
        { id: "00:00:00", name: "00:00" },
        { id: "01:00:00", name: "01:00" },
        { id: "02:00:00", name: "02:00" },
        { id: "03:00:00", name: "03:00" },
        { id: "04:00:00", name: "04:00" },
        { id: "05:00:00", name: "05:00" },
        { id: "06:00:00", name: "06:00" },
        { id: "07:00:00", name: "07:00" },
        { id: "08:00:00", name: "08:00" },
        { id: "09:00:00", name: "09:00" },
        { id: "10:00:00", name: "10:00" },
        { id: "11:00:00", name: "11:00" },
        { id: "12:00:00", name: "12:00" },
        { id: "13:00:00", name: "13:00" },
        { id: "14:00:00", name: "14:00" },
        { id: "15:00:00", name: "15:00" },
        { id: "16:00:00", name: "16:00" },
        { id: "17:00:00", name: "17:00" },
        { id: "18:00:00", name: "18:00" },
        { id: "19:00:00", name: "19:00" },
        { id: "20:00:00", name: "20:00" },
        { id: "21:00:00", name: "21:00" },
        { id: "22:00:00", name: "22:00" },
        { id: "23:00:00", name: "23:00" },
    ];

    const otherDaysTimeList = [
        { id: "08:00:00", name: "08:00" },
        { id: "09:00:00", name: "09:00" },
        { id: "10:00:00", name: "10:00" },
        { id: "11:00:00", name: "11:00" },
        { id: "12:00:00", name: "12:00" },
        { id: "13:00:00", name: "13:00" },
        { id: "14:00:00", name: "14:00" },
        { id: "15:00:00", name: "15:00" },
        { id: "16:00:00", name: "16:00" },
        { id: "17:00:00", name: "17:00" },
        { id: "18:00:00", name: "18:00" },
        { id: "19:00:00", name: "19:00" },
        { id: "20:00:00", name: "20:00" },
        { id: "21:00:00", name: "21:00" },
        { id: "22:00:00", name: "22:00" },
        { id: "23:00:00", name: "23:00" },
    ];

    const [startTimeList, setStartTimeList] = useState([]);
    const [endTimeList, setEndTimeList] = useState([]);
    //times
    const [times, setTimes] = useState({
        firstDayTime: timeList.find(time => time.id === itineraryInformation.firstDayTime),
        lastDayTime: timeList.find(time => time.id === itineraryInformation.lastDayTime),
        startTime: otherDaysTimeList.find(time => time.id === itineraryInformation.startTime),
        endTime: otherDaysTimeList.find(time => time.id === itineraryInformation.endTime),
    });

    const [timesChanges, setTimesChanges] = useState({
        firstDayTime: timeList.find(time => time.id === itineraryInformation.firstDayTime),
        lastDayTime: timeList.find(time => time.id === itineraryInformation.lastDayTime),
        startTime: otherDaysTimeList.find(time => time.id === itineraryInformation.startTime),
        endTime: otherDaysTimeList.find(time => time.id === itineraryInformation.endTime),
    });

    useEffect(() => {
        const startTimeIndex = otherDaysTimeList.findIndex(item => item.id === times.startTime.id);
        setStartTimeList(otherDaysTimeList.filter((item, index) => index < 11));
        setEndTimeList(otherDaysTimeList.filter((item, index) => index > startTimeIndex + 4));
    }, [times]);

    useEffect(() => {
        if (!!endTimeList.length) {
            const endTime = endTimeList?.find(item => item?.id === times?.endTime?.id);
            if (!endTime) {
                handleAutoCompleteChange(endTimeList[0], "endTime");
            }
        }
    }, [endTimeList]);

    //handle time change
    const [isActive, setIsActive] = useState({ plus: true, minus: true });
    const handleChange = (type, name) => {
        if (name === "firstDayTime" || name === "lastDayTime") {
            const itemIndex = timeList.findIndex(timeItem => timeItem.id === timesChanges[name].id);
            if (type === "plus") {
                if (itemIndex === 23) {
                    setIsActive(values => ({ ...values, plus: false, minus: true }));
                    return false;
                } else setIsActive(values => ({ ...values, plus: true, minus: true }));
                setTimesChanges(values => ({ ...values, [name]: timeList[itemIndex + 1] }));
            }

            if (type === "minus") {
                if (itemIndex === 0) {
                    setIsActive(values => ({ ...values, plus: true, minus: false }));
                    return false;
                } else setIsActive(values => ({ ...values, plus: true, minus: true }));
                setTimesChanges(values => ({ ...values, [name]: timeList[itemIndex - 1] }));
            }
        }
        if (name === "startTime" || name === "endTime") {
            const itemIndex = otherDaysTimeList.findIndex(timeItem => timeItem.id === timesChanges[name].id);
            if (parseInt(timesChanges.endTime.id) - parseInt(timesChanges.startTime.id) > 5) {
                if (type === "plus") {
                    if (itemIndex === 15) {
                        setIsActive(values => ({ ...values, plus: false, minus: true }));
                        return false;
                    } else setIsActive(values => ({ ...values, plus: true, minus: true }));
                    setTimesChanges(values => ({ ...values, [name]: otherDaysTimeList[itemIndex + 1] }));
                }

                if (type === "minus") {
                    if (itemIndex === 0) {
                        setIsActive(values => ({ ...values, plus: true, minus: false }));
                        return false;
                    } else setIsActive(values => ({ ...values, plus: true, minus: true }));
                    setTimesChanges(values => ({ ...values, [name]: otherDaysTimeList[itemIndex - 1] }));
                }
            } else {
                toast.error(t("Toast Messages.Time Difference Requirement"), { toastId: "custom-id-yes" });
            }
        }
    };

    //confirm
    const handleConfirm = () => {
        setTimes(timesChanges);
        handleClose();
    };

    //handle change for mobile
    const [timeSearchValue, setTimeSearchValue] = useState("");
    const handleAutoCompleteChange = (value, name) => {
        setTimes(values => ({ ...values, [name]: value }));
    };

    //Popover
    const [anchorEl, setAnchorEl] = useState(null);
    const [openStyle, setOpenStyle] = useState({ firstDayTime: false, lastDayTime: false, startTime: false, endTime: false });

    const handleClick = (event, type) => {
        setAnchorEl(event.currentTarget);
        setOpenStyle(values => ({ ...values, [type]: true }));
    };

    const handleClose = () => {
        setAnchorEl(null);
        setOpenStyle(false);
    };

    const openPopover = Boolean(anchorEl);
    const id = openPopover ? "simple-popover" : undefined;

    //modal
    const handleModalClose = () => {
        setOpen(false);
        navigate(-1);
        searchParams.delete("action");
        setSearchParams(searchParams);
    };

    return (
        <Grid container item xs={12}>
            <Grid className="itinerary-time" container item xs={12} display={{ xs: "none", md: "flex" }} alignItems="center">
                <Container maxWidth="xl">
                    <Grid container item xs={12} justifyContent="space-between" alignItems="cenetr">
                        <Grid container item xs={9} justifyContent="space-between">
                            <Clock size="70" color="var(--teal-color-01)" variant="Bold" />
                            <Grid container item xs={5.25} justifyContent="space-between">
                                <Grid
                                    className={`itinerary-time__section ${openStyle.firstDayTime ? "open" : ""}`}
                                    container
                                    item
                                    xs={5.8}
                                    rowGap={1}
                                    direction="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    onClick={event => handleClick(event, "firstDayTime")}
                                >
                                    <span className="itinerary-time__title">{t("firstDayStartTime")}</span>
                                    <span className="itinerary-time__time">
                                        <Edit2 color="var(--teal-color-18)" /> {times?.firstDayTime?.name}
                                    </span>
                                </Grid>
                                <Grid
                                    className={`itinerary-time__section ${openStyle.lastDayTime ? "open" : ""}`}
                                    container
                                    item
                                    xs={5.8}
                                    rowGap={1}
                                    direction="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    onClick={event => handleClick(event, "lastDayTime")}
                                >
                                    <span className="itinerary-time__title">{t("lastDayEndTime")}</span>
                                    <span className="itinerary-time__time">
                                        <Edit2 color="var(--teal-color-18)" /> {times?.lastDayTime?.name}
                                    </span>
                                </Grid>
                            </Grid>
                            <Grid container item xs={5.25} justifyContent="space-between">
                                <Grid
                                    className={`itinerary-time__section ${openStyle.startTime ? "open" : ""}`}
                                    container
                                    item
                                    xs={5.8}
                                    rowGap={1}
                                    direction="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    onClick={event => handleClick(event, "startTime")}
                                >
                                    <span className="itinerary-time__title">{t("startTime")}</span>
                                    <span className="itinerary-time__time">
                                        <Edit2 color="var(--teal-color-18)" /> {times?.startTime?.name}
                                    </span>
                                </Grid>
                                <Grid
                                    className={`itinerary-time__section ${openStyle.endTime ? "open" : ""}`}
                                    container
                                    item
                                    xs={5.8}
                                    rowGap={1}
                                    direction="column"
                                    justifyContent="center"
                                    alignItems="center"
                                    onClick={event => handleClick(event, "endTime")}
                                >
                                    <span className="itinerary-time__title">{t("endTime")}</span>
                                    <span className="itinerary-time__time">
                                        <Edit2 color="var(--teal-color-18)" /> {times?.endTime?.name}
                                    </span>
                                </Grid>
                            </Grid>
                        </Grid>
                        <BasePopover
                            id={id}
                            open={openPopover}
                            anchorEl={anchorEl}
                            handleClose={handleClose}
                            width="15%"
                            vertical="bottom"
                            horizontal="right"
                        >
                            <Grid container item xs={12}>
                                {Object.keys(openStyle).map((item, index) => {
                                    return (
                                        <Grid key={index} container item xs={12} rowGap={1} direction="column" alignItems="center">
                                            {!!openStyle[item] && (
                                                <ArrowUp2
                                                    color={
                                                        !isActive.plus || timeList.findIndex(timeItem => timeItem.id === timesChanges[item].id) === 23
                                                            ? "var(--gray-color)"
                                                            : "var(--primary-color)"
                                                    }
                                                    onClick={() => handleChange("plus", item)}
                                                    cursor="pointer"
                                                />
                                            )}
                                            <span className="itinerary-time__times-changes">{!!openStyle[item] && timesChanges[item].name}</span>
                                            {!!openStyle[item] && (
                                                <ArrowDown2
                                                    color={
                                                        !isActive.minus || timeList.findIndex(timeItem => timeItem.id === timesChanges[item].id) === 0
                                                            ? "var(--gray-color)"
                                                            : "var(--primary-color)"
                                                    }
                                                    onClick={() => handleChange("minus", item)}
                                                    cursor="pointer"
                                                />
                                            )}
                                        </Grid>
                                    );
                                })}

                                <Grid container item xs={12} justifyContent="space-between">
                                    <Grid container item xs={5.9}>
                                        <BaseButton text="تایید" color="green" radius="radius-10" size="small" handleClick={handleConfirm} />
                                    </Grid>
                                    <Grid container item xs={5.9}>
                                        <BaseButton text="انصراف" color="outline-gray" radius="radius-10" size="small" handleClick={handleClose} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </BasePopover>

                        <Grid container item xs={2} alignItems="center">
                            <BaseButton
                                text={t("regenerating")}
                                color="outline-white"
                                size="large"
                                radius="radius-10"
                                handleClick={() => handleTimeChange(times.firstDayTime, times.lastDayTime, times.startTime, times.endTime)}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Grid>

            <Grid className="itinerary-time" container item xs={12} display={{ xs: "flex", md: "none" }}>
                <Dialog
                    open={open}
                    onClose={handleModalClose}
                    keepMounted
                    fullScreen
                    className="itinerary-time"
                    sx={{
                        top: "auto",
                    }}
                >
                    <Slide direction="up" in={open}>
                        <Grid container item xs={12} rowGap={3} mb={2}>
                            <Grid className="itinerary-time__header" container item xs={12}>
                                <Grid container item xs={1} justifyContent="center">
                                    <ArrowDown2 size="24" color="var(--light-color)" onClick={handleModalClose} cursor="pointer" />
                                </Grid>
                                <Grid className="itinerary-time__title" container item xs={10} justifyContent="center">
                                    <span>{t("setTime")}</span>
                                </Grid>
                                <Grid container item xs={1}></Grid>
                            </Grid>

                            <Grid container item xs={12} justifyContent="center" alignItems="center">
                                <Grid
                                    className="itinerary-time__container"
                                    container
                                    item
                                    xs={10}
                                    rowGap={2}
                                    justifyContent="space-between"
                                    alignItems="flex-end"
                                >
                                    <Grid container item xs={5.8} md={2}>
                                        <AutoCompleteComponent
                                            variant="standard"
                                            label={t("firstDayStartTime")}
                                            options={timeList}
                                            name={"firstDayTime"}
                                            defaultValue={times.firstDayTime}
                                            handleChange={handleAutoCompleteChange}
                                            setInputValue={setTimeSearchValue}
                                            control={control}
                                            width={350}
                                        />
                                    </Grid>
                                    <Grid container item xs={5.8} md={2}>
                                        <AutoCompleteComponent
                                            variant="standard"
                                            label={t("lastDayEndTime")}
                                            options={timeList}
                                            name={"lastDayTime"}
                                            defaultValue={times.lastDayTime}
                                            handleChange={handleAutoCompleteChange}
                                            setInputValue={setTimeSearchValue}
                                            control={control}
                                            width={350}
                                        />
                                    </Grid>
                                    <Grid container item xs={5.8} md={2}>
                                        <AutoCompleteComponent
                                            variant="standard"
                                            label={t("startTime")}
                                            options={startTimeList}
                                            name={"startTime"}
                                            defaultValue={times.startTime}
                                            handleChange={handleAutoCompleteChange}
                                            setInputValue={setTimeSearchValue}
                                            control={control}
                                            width={350}
                                        />
                                    </Grid>
                                    <Grid container item xs={5.8} md={2}>
                                        <AutoCompleteComponent
                                            variant="standard"
                                            label={t("endTime")}
                                            options={endTimeList}
                                            name={"endTime"}
                                            defaultValue={times.endTime}
                                            handleChange={handleAutoCompleteChange}
                                            setInputValue={setTimeSearchValue}
                                            control={control}
                                        />
                                    </Grid>
                                    <Grid container item xs={12} md={2}>
                                        <BaseButton
                                            text={t("regenerating")}
                                            color="secondary"
                                            size="large"
                                            radius="radius-10"
                                            handleClick={() =>
                                                handleTimeChange(times.firstDayTime, times.lastDayTime, times.startTime, times.endTime)
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Slide>
                </Dialog>
            </Grid>
        </Grid>
    );
}

export default ItineraryTime;
